import { Link, useNavigate } from "react-router-dom";
import { Cog, Truck, Warehouse } from "lucide-react";

import { readLastApp, rememberLastApp } from "@/components/AppSwitcher";
import { UserMenu } from "@/components/UserMenu";
import { useIntegratorMe } from "@/lib/me-query";
import { buildFloorTabletHomeSections } from "@/lib/floorTabletHomeNav";
import {
  readFloorTabletHomePreference,
  writeFloorTabletHomePreference,
} from "@/lib/floorTabletDevice";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { allowedModuleKeysFromMe, appsVisibleInAppSwitcher } from "@/lib/subapp-access";
import { cn } from "@/lib/utils";

const SECTION_ICONS = {
  manufacturing: Cog,
  shipping: Truck,
  warehouse: Warehouse,
} as const;

export function FloorTabletHomePage(): JSX.Element {
  const navigate = useNavigate();
  const { resetNavigationTrail } = useNavigationTrail();
  const { data: me } = useIntegratorMe();
  const moduleKeys = me?.user ? allowedModuleKeysFromMe(me.user) : new Set<string>();
  const sections = buildFloorTabletHomeSections(moduleKeys);
  const allowedApps = appsVisibleInAppSwitcher(me);

  function openFullHub(): void {
    resetNavigationTrail();
    writeFloorTabletHomePreference("off");
    const lastKey = readLastApp();
    const target = allowedApps.find((app) => app.key === lastKey) ?? allowedApps[0];
    if (target != null) {
      rememberLastApp(target.key);
      navigate(target.path, { replace: true });
      return;
    }
    navigate("/access-needed", { replace: true });
  }

  function pinFloorHome(): void {
    writeFloorTabletHomePreference("on");
  }

  const pinned = readFloorTabletHomePreference() === "on";

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">SGS Hub</p>
            <h1 className="text-lg font-semibold text-slate-900">Floor stations</h1>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6 px-4 py-6">
        {sections.length === 0 ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            Your account does not have Manufacturing, Shipping, or Warehouse module access. Use the full hub or ask an
            administrator to enable a floor module.
          </p>
        ) : (
          <div className={cn("grid items-start gap-4", sections.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
            {sections.map((section) => {
              const Icon = SECTION_ICONS[section.moduleKey];
              return (
                <section key={section.moduleKey} className="min-w-0 space-y-3">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 shrink-0 text-brand-700" aria-hidden />
                    <h2 className="text-base font-semibold text-slate-900">{section.title}</h2>
                  </div>
                  <ul className="grid gap-3">
                    {section.links.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          onClick={resetNavigationTrail}
                          className={cn(
                            "flex min-h-14 flex-col justify-center rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm",
                            "text-brand-800 transition-colors hover:border-brand-300 hover:bg-brand-50/40 active:bg-brand-50",
                          )}
                        >
                          <span className="text-base font-semibold leading-snug">{link.label}</span>
                          {link.description ? (
                            <span className="mt-0.5 line-clamp-2 text-sm text-slate-600">{link.description}</span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <div className="mt-auto flex flex-col gap-2 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={openFullHub}
            className="min-h-12 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Open full hub
          </button>
          <button
            type="button"
            onClick={pinFloorHome}
            className={cn(
              "min-h-10 rounded-lg px-4 text-sm",
              pinned ? "text-slate-500" : "text-brand-700 hover:text-brand-800",
            )}
            disabled={pinned}
          >
            {pinned ? "This tablet always opens floor stations first" : "Always open floor stations on this tablet"}
          </button>
        </div>
      </main>
    </div>
  );
}
